import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileDown, Loader2, ChevronLeft } from "lucide-react";
import { api } from "@/lib/auth";
import { openInvoicePdf } from "@/lib/downloads";
import { fmtDate } from "@/lib/datetime";

function eurFr(n) {
  const v = Number(n || 0);
  if (Math.abs(v - Math.round(v)) < 0.005) return `${Math.round(v)} €`;
  return `${v.toFixed(2).replace(".", ",")} €`;
}

export default function MyInvoices() {
  const [invoices, setInvoices] = useState(null);
  const [filter, setFilter] = useState("");
  const clientOptions = Array.from(new Map((invoices || []).filter((i) => i.billed_to_end_client)
    .map((i) => [i.client_record_id, i.billing?.company_name || `${i.billing?.first_name || ""} ${i.billing?.last_name || ""}`.trim()])).entries());
  const shown = (invoices || []).filter((i) => !filter || (filter === "own" ? !i.billed_to_end_client : i.client_record_id === filter));

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/me/invoices");
        setInvoices(data.invoices || []);
      } catch { setInvoices([]); }
    })();
  }, []);

  return (
    <div className="cs-cream py-14 min-h-[80vh]" data-testid="page-my-invoices">
      <div className="max-w-4xl mx-auto px-6">
        <Link to="/espace" className="text-[13px] font-mono text-neutral-500 hover:text-black inline-flex items-center gap-1">
          <ChevronLeft size={14} /> Retour à mon espace
        </Link>
        <div className="cs-eyebrow text-neutral-500 mt-6">Espace client</div>
        <h1 className="cs-h1 mt-2 text-4xl">Mes factures</h1>

        {clientOptions.length > 0 && (
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="cs-input mt-6 max-w-xs" data-testid="invoices-client-filter">
            <option value="">Toutes les factures</option>
            <option value="own">Facturées à mon nom</option>
            {clientOptions.map(([id, n]) => <option key={id} value={id}>Facturées à {n}</option>)}
          </select>
        )}
        <div className="mt-8">
          {invoices === null && (
            <div className="flex items-center gap-2 text-sm text-neutral-500"><Loader2 size={14} className="animate-spin" /> Chargement…</div>
          )}
          {invoices?.length === 0 && (
            <div className="cs-card p-8 text-center text-sm text-neutral-600" data-testid="invoices-empty">
              Aucune facture pour le moment.
              <div className="mt-1 text-[12px] font-mono">Vos factures apparaîtront ici dès qu'un règlement sera confirmé.</div>
            </div>
          )}
          {invoices?.length > 0 && (
            <div className="cs-card p-0 overflow-hidden" data-testid="invoices-list">
              {/* Desktop header — hidden on mobile */}
              <div className="hidden sm:grid grid-cols-[minmax(0,140px)_1fr_120px_120px] gap-4 px-5 py-3 border-b border-black/15 text-[11px] font-mono uppercase tracking-[0.14em] text-neutral-500">
                <div>Numéro</div>
                <div>Type · Commande</div>
                <div className="text-right">Montant</div>
                <div className="text-right">PDF</div>
              </div>
              {shown.map((i) => (
                <div
                  key={i.id}
                  className="flex flex-col gap-3 px-5 py-4 border-b border-black/10 sm:grid sm:grid-cols-[minmax(0,140px)_1fr_120px_120px] sm:gap-4 sm:items-center"
                  data-testid={`invoice-row-${i.number}`}
                >
                  <div className="flex items-start justify-between sm:block">
                    <div className="font-mono text-sm">{i.number}</div>
                    <div className="text-right sm:hidden font-mono text-base">{eurFr(i.amount)}</div>
                  </div>
                  <div className="text-sm">
                    <div>{i.type_label}</div>
                    {i.billed_to_end_client && <div className="text-[12px] text-[#0E7C90]">Facturée à {i.billing?.company_name || `${i.billing?.first_name || ""} ${i.billing?.last_name || ""}`.trim()}</div>}
                    <div className="text-[12px] font-mono text-neutral-500">
                      {i.description || i.order_reference || "—"} · {i.issued_at ? fmtDate(i.issued_at) : "—"}
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      <span
                        className={`text-[10px] font-mono uppercase tracking-[0.12em] px-1.5 py-0.5 border ${i.payment_status === "paid" ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-amber-500 bg-amber-50 text-amber-700"}`}
                        data-testid={`invoice-pay-status-${i.number}`}
                      >
                        {i.payment_status === "paid" ? "Payée" : "À régler"}
                      </span>
                      {i.payment_status === "paid" && i.paid_at && (
                        <span className="text-[11px] font-mono text-neutral-500">
                          Payée le {fmtDate(i.paid_at)}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="hidden sm:block text-right font-mono">{eurFr(i.amount)}</div>
                  <div className="sm:text-right">
                    <button
                      type="button"
                      onClick={() => openInvoicePdf(i.id)}
                      className="cs-btn-ghost !text-black !border-black !py-2 !px-3 text-[13px] inline-flex items-center gap-1 w-full sm:w-auto justify-center"
                      data-testid={`download-invoice-${i.number}`}
                    >
                      <FileDown size={13} /> Télécharger le PDF
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
